// ── Patient chart panel device cache ─────────────────────────────
//
// In-memory, PHI-safe cache for the chart context panel's JIT reads.
// Every chart tab is a live eCW round-trip through the backend, so the
// panel would otherwise re-fire a multi-second vendor call on every tab
// flip. This module gives it the same two-layer treatment as
// `noteTemplateService`:
//
//   1. `inFlight` — de-dupes overlapping loads for the same key, so a
//      rapid tab flip away-and-back joins the pending request instead
//      of firing a duplicate.
//   2. `resultCache` — caches successful results for CHART_CACHE_TTL_MS.
//      Failures are deliberately NOT cached so a transient EHR blip
//      isn't pinned for the whole TTL; the next tab visit retries.
//
// Keys are `${ehrPatientId}:${op}` — by OP, not by tab — because two
// tab pairs share one backend call each (Allergies + Immunizations ride
// `imm-allergies`; Labs & imaging is one `orders` worklist read). One
// fetch fills both tabs.
//
// PHI hygiene: memory only — never localStorage/sessionStorage. The
// cache is scoped to ONE patient at a time (`setActiveChartPatient`
// purges every other patient's entries when the panel opens on someone
// new) and wiped entirely on sign-out via `clearPatientChartCache()`
// from `AuthContext.logout`.

import {logger, LogFlow, describeError} from './logger';

const CHART_CACHE_TTL_MS = 5 * 60 * 1000;
const log = logger.scope(LogFlow.PatientChart);

// Chart data ops, one per underlying backend call. Prior-note reads are
// keyed per encounter (`note:{encounterId}`) so drilling out and back
// into the same visit is instant too.
export type ChartCacheOp =
  | 'encounters'
  | 'problem-list'
  | 'medications'
  | 'imm-allergies'
  | 'orders'
  | `note:${string}`;

interface ChartCacheEntry {
  value: any;
  cachedAt: number;
}

const resultCache = new Map<string, ChartCacheEntry>();
const inFlight = new Map<string, Promise<any>>();
let activePatientId: string | null = null;

function cacheKey(ehrPatientId: string, op: ChartCacheOp): string {
  return `${ehrPatientId}:${op}`;
}

function resultSummary(value: any): Record<string, unknown> {
  if (Array.isArray(value)) return {result_count: value.length};
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, unknown> = {};
  const countKeys = [
    'problems',
    'groups',
    'medications',
    'allergies',
    'immunizations',
    'orders',
    'sections',
  ];
  for (const key of countKeys) {
    if (Array.isArray(value[key])) out[`${key}_count`] = value[key].length;
  }
  return out;
}

/**
 * Scope the cache to one patient. Called by the chart panel whenever it
 * opens / switches patients; entries (and pending loads) belonging to
 * any OTHER patient are dropped so at most one patient's chart ever
 * sits in memory.
 */
export function setActiveChartPatient(ehrPatientId: string | null): void {
  const next = ehrPatientId || null;
  if (activePatientId === next) return;
  const previous = activePatientId;
  activePatientId = next;
  const prefix = next ? `${next}:` : null;
  for (const key of Array.from(resultCache.keys())) {
    if (!prefix || !key.startsWith(prefix)) resultCache.delete(key);
  }
  for (const key of Array.from(inFlight.keys())) {
    if (!prefix || !key.startsWith(prefix)) inFlight.delete(key);
  }
  log.debug('active_patient_changed', {
    ehr_patient_id: next,
    had_previous_patient: Boolean(previous),
    cache_entry_count: resultCache.size,
    in_flight_count: inFlight.size,
  });
}

/**
 * Synchronous cache probe. Returns the entry only while fresh (within
 * TTL) so callers can seed initial state without a loading flash; stale
 * entries read as misses and trigger a refetch.
 */
export function readChartCache<T = any>(
  ehrPatientId: string,
  op: ChartCacheOp,
): {value: T; cachedAt: number} | null {
  const entry = resultCache.get(cacheKey(ehrPatientId, op));
  if (!entry) return null;
  if (Date.now() - entry.cachedAt >= CHART_CACHE_TTL_MS) return null;
  return entry as {value: T; cachedAt: number};
}

/**
 * Load one op's data through the cache: fresh hit → resolved value with
 * no network; pending load → join it; otherwise run `fetcher`, cache on
 * success, and share the promise with concurrent callers. `force: true`
 * (the per-tab Refresh affordance) bypasses both read layers but still
 * registers in-flight so concurrent readers coalesce onto the refresh.
 */
export async function loadChartData<T = any>(
  ehrPatientId: string,
  op: ChartCacheOp,
  fetcher: () => Promise<T>,
  {force = false} = {},
): Promise<T> {
  const key = cacheKey(ehrPatientId, op);

  if (!force) {
    const cached = readChartCache<T>(ehrPatientId, op);
    if (cached) {
      log.debug('data_cache_hit', {
        ehr_patient_id: ehrPatientId,
        chart_op: op,
        cache_age_ms: Date.now() - cached.cachedAt,
      });
      return cached.value;
    }
    const pending = inFlight.get(key);
    if (pending) {
      log.debug('data_joined_in_flight', {
        ehr_patient_id: ehrPatientId,
        chart_op: op,
      });
      return pending;
    }
  }

  const startedAt = Date.now();
  log.info('data_load_started', {
    ehr_patient_id: ehrPatientId,
    chart_op: op,
    force_refresh: force,
  });
  const promise = (async () => fetcher())();
  inFlight.set(key, promise);
  try {
    const value = await promise;
    resultCache.set(key, {value, cachedAt: Date.now()});
    log.info('data_load_succeeded', {
      ehr_patient_id: ehrPatientId,
      chart_op: op,
      force_refresh: force,
      duration_ms: Date.now() - startedAt,
      ...resultSummary(value),
    });
    return value;
  } catch (error) {
    log.error('data_load_failed', {
      ehr_patient_id: ehrPatientId,
      chart_op: op,
      force_refresh: force,
      duration_ms: Date.now() - startedAt,
      error_status: (error as any)?.status ?? null,
      ...describeError(error),
    });
    throw error;
  } finally {
    if (inFlight.get(key) === promise) inFlight.delete(key);
  }
}

/** Wipe everything. Called on sign-out (and available to tests). */
export function clearPatientChartCache(): void {
  log.debug('cache_cleared', {
    cache_entry_count: resultCache.size,
    in_flight_count: inFlight.size,
  });
  resultCache.clear();
  inFlight.clear();
  activePatientId = null;
}
